import { z } from "zod";
/**
 * The flag has four values, "off", "remember", "control", and "on":
 *   1. "off" and "control": don't remember the user's state, always open to project chat
 *   2. "on" and "remember": first time a user opens a particular document, open to the project chat, after that remember which tab they're on last and open to that tab.
 *
 * The reason there are four states is so that we can enable and disable the flag for anybody we want, while also allowing Dirk to have full control of the experiment groups (which are "remember" and "control").
 */
export const rememberPanelConfigVariants = ["on", "off", "remember", "control"];
const RememberPanelConfigEnum = z.enum(rememberPanelConfigVariants);
export const performanceMetricsVariants = ["report", "report-profiling", "disabled"];
const PerformanceMetricsEnum = z.enum(performanceMetricsVariants);
export const trackedFlagsSchema = z
    .array(z.string())
    .optional()
    .default([])
    .transform((inputValues) => {
    try {
        const result = {};
        for (const inputValue of inputValues) {
            const baseFlagName = inputValue.split(".")[0];
            const entry = Object.entries(featureFlags).find(([, value]) => value === baseFlagName);
            if (entry) {
                const [fluxName] = entry;
                if (!result[fluxName]) {
                    result[fluxName] = [];
                }
                result[fluxName].push(inputValue);
            }
        }
        return result;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error processing tracked flags:", error, "Input:", inputValues);
        return {};
    }
});
/**
 * For LaunchDarkly admin pages, go to -> https://app.launchdarkly.com/flux-app/production/features/ .
 *
 * Intentionally chose object of strings over enum based on official typescript recommendation
 * @see https://www.typescriptlang.org/docs/handbook/enums.html#objects-vs-enums
 *
 * @remarks
 * In order to send a feature flag value to Amplitude (e.g. for AB tests), you must prefix the *key* with
 * "segment" or "ab-test". Alternatively, you can add it to the "tracked-flags" feature flag configuration
 * in LaunchDarkly. See `getFeatureFlags` in `AnalyticsStorage`.
 **/
const featureFlags = {
    // NOTE: LD seems to prefer kebab-case key values, that's what's autogenerated
    apiKeys: "api-keys",
    showKiCadExportMenu: "show-ki-cad-export-menu-item",
    showNativeGerberExportMenuItem: "show-native-gerber-export-menu-item",
    showAdvancedExportFormats: "show-advanced-export-formats",
    showAutolayoutJsonExportMenuItem: "show-autolayout-export-menu-item",
    showJEP30ExportMenuItem: "show-jep-30-export-menu-item",
    showJEP30ImportMenuItem: "show-jep-30-import-menu-item",
    /**
     * @deprecated use import_edif entitlement instead
     */
    showEdifImportMenuItem: "show-edif-import-menu-item",
    /**
     * @deprecated use import_altium entitlement instead
     */
    showAltiumImportMenuItem: "show-altium-import-menu-item",
    enableDatasheetImports: "enable-datasheet-imports",
    newRemoteUserNotification: "new-remote-user-notification",
    disableActionRecordWrites: "disable-action-record-writes",
    disableAutoSave: "disable-autosave",
    disableMainThreadScheduling: "disable-main-thread-scheduling",
    showGerberPreviewExportMenuItem: "show-gerber-preview-export-menu-item",
    disablePcbLayoutEngineWorker: "disable-pcb-layout-engine-worker",
    enableEnvSwitchHotkeys: "enable-env-switch-hotkeys",
    drcEnabledCheckers: "drc-enabled-checkers",
    showSyncErrors: "show-sync-errors",
    freePlanMigrationShowExpiryMessaging: "free-plan-migration-show-expiry-messaging",
    segmentFreePlanMigrationShowAnnouncementDialog: "free-plan-migration-show-announcement-dialog",
    organizationsSso: "organizations-sso",
    segmentCancelPlanDialog: "cancel-plan-dialog",
    copilotAuditLogEnabled: "copilot-audit-log-enabled",
    segmentLegacyProPlanPrice: "ab-test-more-pro-tier-pricing-2",
    segmentLegacyUltraPlanPrice: "ab-test-new-ultra-plan",
    segmentProPlanPrice: "ab-test-pricing-from-202410-pro",
    segmentStarterPlanPrice: "ab-test-pricing-from-202410-starter",
    segmentOrganizationPaidPlanPrice: "ab-test-pricing-organization-paid",
    enablePcb3dReflections: "enable-pcb-3d-reflections",
    enablePcbBatchedSilkRectangle: "enable-pcb-batched-silk-rectangle",
    enablePcbBatchedSilkLine: "enable-pcb-batched-silk-line",
    enablePcbOrphanNodeRemoval: "enable-pcb-orphan-node-removal",
    enablePcbControlledBaking: "enable-pcb-controlled-baking",
    enablePcbImprovedSelectionUX: "enable-pcb-improved-selection-ux",
    enablePcbTintedSelection: "enable-pcb-tinted-selection",
    disablePcbLayerZPosition: "disable-pcb-layer-z-position",
    shadowbanPanel: "shadowban-panel",
    segmentNewProjectDialog: "ab-test-new-project-dialog",
    enablePcbFillPartitionAcceleration: "pcb-fill-generation-partition-accelearion",
    copilotSpreadsheetImport: "copilot-spreadsheet-import",
    enableInvitesByEmail: "enable-invites-by-email",
    customSizedParametricSymbols: "custom-size-for-parametric-symbols",
    searchClient: "search-client",
    searchClientForPartLibrary: "search-client-for-part-library",
    segmentRememberPanelConfig: "remember-panel-config",
    segmentOnboardingProjectSetName: "onboarding-project-set",
    onboardingProjectSetsList: "onboarding-project-sets-list",
    trialTypePaymentUpFrontRatio: "trial-type-payment-up-front-ratio",
    paidCreditConsentRequired: "paid-credit-consent-required",
    pcbAutoLayoutJobConfiguration: "pcb-auto-layout-job-configuration",
    enableDesignatorMapping: "enable-designator-mapping",
    showLearnFluxOnProfile: "showLearnFluxOnProfile",
    segmentPlanButtonConfig: "planButtonConfig",
    showNewAutoPlacementUi: "show-new-auto-placement-ui",
    segmentExportManufacturingFilesDialog: "showExportManufacturingFilesDialog",
    segmentBookACallWithFlux: "bookACallWithFlux",
    segmentJoinSlackCommunity: "joinSlackCommunity",
    trackedFlags: "tracked-flags",
    showAutoLayoutDrcReview: "show-auto-layout-drc-review",
    showHiddenDrcChecks: "show-hidden-drc-checks",
    libraryToolPlanAndSolve: "libraryToolPlanAndSolve",
    libraryToolSearchConfig: "library-tool-search-config",
    streamingBufferConfig: "copilot-streaming-buffer-config",
    copilotMessageThoughts: "copilot-message-thoughts",
    copilotMessageThoughtsShowReasoning: "copilot-message-thoughts-show-reasoning",
    internalModerationTools: "internal-moderation-tools",
    copilotDisableFollowupResponses: "copilot_disable_followup_when_action",
    performanceMetrics: "performance-metrics",
    autoDetermineNetType: "auto-determine-net-type",
    drawerTabConfiguration: "drawer-tab-configuration",
    segmentShowNewInspectorTab: "show-new-inspector-tab",
    autopilotUseSkipper: "autopilot-use-skipper",
    showSkipperSelectionOption: "show-skipper-selection-option",
    autopilotUseOrbit: "autopilot-use-orbit",
    showOrbitSelectionOption: "show-orbit-selection-option",
    orbitKnowledgeToSystemQueue: "orbit-knowledge-to-system-queue",
    choosePlanViewExtended: "choose-plan-view-extended",
    chatEmptyStateVideoUrl: "chat-empty-state-video-url",
    creditPricingExperiment: "credit-pricing-experiment",
    mobileDontLoadEditors: "mobile-dont-load-editors",
    trialReactivationConfig: "trial-reactivation-config",
    showDocEditor: "show-doc-editor",
    mcpServerEnabled: "mcp-server-enabled",
    mcpWhitelistedAccounts: "mcp-whitelisted-accounts",
    profileProjectCardEnrichment: "profile-project-card-enrichment",
    showProfileProjectCardPriceCounter: "show-profile-project-card-price-counter",
    showDocumentPricing: "show-document-pricing",
    endTrialOnFreeCreditsExhausted: "end-trial-on-free-credits-exhausted",
    agentToolInternalFeedback: "agent-tool-internal-feedback",
};
export { featureFlags };
//# sourceMappingURL=FeatureFlags.js.map